import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiFetch } from '../../api/client';

const defaultMeta = {
  page: 1,
  pageSize: 10,
  totalItems: 0
};

const defaultQuery = {
  page: 1,
  pageSize: 10,
  search: '',
  status: 'ALL',
  sortBy: 'id',
  sortDir: 'desc'
};

const initialState = {
  items: [],
  meta: defaultMeta,
  loading: false,
  error: null,
  query: defaultQuery,
  detail: null,
  detailLoading: false,
  detailError: null,
  saveStatus: 'idle',
  saveError: null,
  secretStatus: 'idle',
  secretError: null
};

const buildQueryString = (params = {}) => {
  const merged = { ...defaultQuery, ...params };
  const query = new URLSearchParams();
  if (merged.page != null) query.set('page', merged.page);
  if (merged.pageSize != null) query.set('pageSize', merged.pageSize);
  if (merged.search) query.set('search', merged.search);
  if (merged.status && merged.status !== 'ALL') query.set('status', merged.status);
  if (merged.sortBy) query.set('sortBy', merged.sortBy);
  if (merged.sortDir) query.set('sortDir', merged.sortDir);
  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

export const fetchSourceChannels = createAsyncThunk(
  'sourceChannels/fetchSourceChannels',
  async (params, { rejectWithValue }) => {
    try {
      const queryString = buildQueryString(params);
      const data = await apiFetch(`/api/source-channels${queryString}`);
      const items = Array.isArray(data?.data) ? data.data : data?.data?.items || [];
      const meta = data?.meta || defaultMeta;
      return {
        items,
        meta,
        query: {
          page: params?.page ?? defaultQuery.page,
          pageSize: params?.pageSize ?? defaultQuery.pageSize,
          search: params?.search ?? defaultQuery.search,
          status: params?.status ?? defaultQuery.status,
          sortBy: params?.sortBy ?? defaultQuery.sortBy,
          sortDir: params?.sortDir ?? defaultQuery.sortDir
        }
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch source channels.');
    }
  }
);

export const fetchSourceChannelById = createAsyncThunk(
  'sourceChannels/fetchSourceChannelById',
  async (id, { rejectWithValue }) => {
    try {
      if (id === null || id === undefined) throw new Error('Source channel ID is required.');
      const data = await apiFetch(`/api/source-channels/${id}`);
      return data?.data || null;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load source channel.');
    }
  }
);

export const createSourceChannel = createAsyncThunk(
  'sourceChannels/createSourceChannel',
  async (payload, { rejectWithValue }) => {
    try {
      const data = await apiFetch('/api/source-channels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      return data?.data || payload;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create source channel.');
    }
  }
);

export const updateSourceChannel = createAsyncThunk(
  'sourceChannels/updateSourceChannel',
  async ({ id, ...payload }, { rejectWithValue }) => {
    try {
      if (id === null || id === undefined) throw new Error('Source channel ID is required.');
      const data = await apiFetch(`/api/source-channels/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      return data?.data || { id, ...payload };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update source channel.');
    }
  }
);

export const updateSourceChannelStatus = createAsyncThunk(
  'sourceChannels/updateSourceChannelStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      if (id === null || id === undefined) throw new Error('Source channel ID is required.');
      const data = await apiFetch(`/api/source-channels/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      return data?.data || { id, status };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update status.');
    }
  }
);

export const generateSourceChannelSecret = createAsyncThunk(
  'sourceChannels/generateSourceChannelSecret',
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiFetch('/api/source-channels/generate-secret', {
        method: 'POST'
      });
      return data?.data?.secretKey || data?.data?.secret_key || data?.secretKey || '';
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to generate secret key.');
    }
  }
);

const sourceChannelsSlice = createSlice({
  name: 'sourceChannels',
  initialState,
  reducers: {
    clearSourceChannelDetail: (state) => {
      state.detail = null;
      state.detailLoading = false;
      state.detailError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSourceChannels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSourceChannels.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.meta = action.payload.meta;
        state.query = action.payload.query;
        state.error = null;
      })
      .addCase(fetchSourceChannels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch source channels.';
      })
      .addCase(fetchSourceChannelById.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchSourceChannelById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.detail = action.payload;
      })
      .addCase(fetchSourceChannelById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload || 'Failed to load source channel.';
      })
      .addCase(createSourceChannel.pending, (state) => {
        state.saveStatus = 'loading';
        state.saveError = null;
      })
      .addCase(createSourceChannel.fulfilled, (state) => {
        state.saveStatus = 'succeeded';
      })
      .addCase(createSourceChannel.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.saveError = action.payload || 'Failed to create source channel.';
      })
      .addCase(updateSourceChannel.pending, (state) => {
        state.saveStatus = 'loading';
        state.saveError = null;
      })
      .addCase(updateSourceChannel.fulfilled, (state) => {
        state.saveStatus = 'succeeded';
      })
      .addCase(updateSourceChannel.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.saveError = action.payload || 'Failed to update source channel.';
      })
      .addCase(updateSourceChannelStatus.fulfilled, (state, action) => {
        const updated = action.payload;
        state.items = state.items.map((item) =>
          item.id === updated.id ? { ...item, status: updated.status } : item
        );
      })
      .addCase(generateSourceChannelSecret.pending, (state) => {
        state.secretStatus = 'loading';
        state.secretError = null;
      })
      .addCase(generateSourceChannelSecret.fulfilled, (state) => {
        state.secretStatus = 'succeeded';
      })
      .addCase(generateSourceChannelSecret.rejected, (state, action) => {
        state.secretStatus = 'failed';
        state.secretError = action.payload || 'Failed to generate secret key.';
      });
  }
});

export const { clearSourceChannelDetail } = sourceChannelsSlice.actions;

export default sourceChannelsSlice.reducer;
